import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { environment } from '@/environments/environment';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import {
  AddBriefDemographicsDto,
  BriefResponseDto,
  DeliveryTypeEnum,
  Piece,
} from '@/app/common/general-types/briefs.dto';
import { NewBriefPricingDto } from '@/app/common/general-types/pricing.dto';
import { BriefService } from '@/app/services/brief/brief.service';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { appliedCount, hiredCount, PersonaDraft, StoredScreeningQuestion } from './briefing.helpers';

/**
 * The Briefing tab's saves. Each section endpoint returns the whole brief, which is written back
 * to every brief signal the app reads so the shell header and other tabs stay in step.
 */
@Injectable({ providedIn: 'root' })
export class BriefingService {
  private readonly http = inject(HttpClient);
  private readonly tabs = inject(ManageBriefTabsService);
  private readonly briefService = inject(BriefService);
  private readonly toast = inject(ToastNotificationService);
  private readonly base = `${environment.shout_api.url}/briefs/edit-brief`;

  readonly brief = this.tabs.focussedBrief;
  readonly hired = computed(() => hiredCount(this.tabs.filterStats()));
  readonly applied = computed(() => appliedCount(this.tabs.filterStats()));

  private briefId(): string {
    const id = this.brief()?.id;
    if (!id) {
      throw new Error('No brief is open');
    }
    return id;
  }

  private post(section: string, body: unknown): Observable<BriefResponseDto> {
    return this.http.post<BriefResponseDto>(`${this.base}/${section}/${this.briefId()}`, body);
  }

  basicInfo(body: { title: string; campaignId: string; productId: string }) {
    return this.post('basic-info', body);
  }

  delivery(deliveryType: DeliveryTypeEnum) {
    return this.post('delivery', { deliveryType });
  }

  pieces(pieces: Piece[]) {
    return this.post('pieces', { pieces });
  }

  documentation(body: { stepsHtml: string; doHtml: string; dontHtml: string; briefDocumentUrl?: string }) {
    return this.post('documentation', body);
  }

  /** Legacy briefs store the video length on the price, so changing it re-prices the brief. */
  pricing(briefPricing: NewBriefPricingDto) {
    return this.http.put<BriefResponseDto>(`${this.base}/${this.briefId()}`, { briefPricing });
  }

  complianceDocs(complianceDocIds: string[]) {
    return this.post('compliance-docs', { complianceDocIds });
  }

  demographics(body: AddBriefDemographicsDto) {
    return this.post('demographics', body);
  }

  creatorPersona(body: PersonaDraft) {
    return this.post('creator-persona', body);
  }

  screeningQuestions(screeningQuestions: StoredScreeningQuestion[]) {
    return this.post('screening-questions', { screeningQuestions });
  }

  apply(brief: BriefResponseDto): void {
    this.tabs.focussedBrief.set(brief);
    this.briefService.focussedBriefSignal.set(brief);
    this.briefService.currentBriefSignal.set(brief);
  }

  /**
   * Runs the saves in order, applies the last brief returned and toasts. Resolves false (and
   * shows the API's message) when any save fails, so the drawer can stay open.
   */
  async save(steps: readonly (() => Observable<BriefResponseDto>)[], message: string): Promise<boolean> {
    try {
      let latest: BriefResponseDto | undefined;
      for (const step of steps) {
        latest = await firstValueFrom(step());
      }
      if (latest) {
        this.apply(latest);
      }
      this.success(message);
      return true;
    } catch (error) {
      this.error(errorMessage(error));
      return false;
    }
  }

  success(message: string): void {
    void this.toast.alert({ title: message, message: '', type: 'success', icon: 'success' });
  }

  error(message: string): void {
    void this.toast.alert({ title: 'Not saved', message, type: 'error', icon: 'error' });
  }
}

/** The API's own message where there is one ("…locked because creators are hired."). */
export function errorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 403) {
      return 'You don’t have access to edit this brief.';
    }
    const raw = error.error?.message;
    const message = Array.isArray(raw) ? raw.join(' ') : raw;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  return 'Something went wrong. Please try again.';
}
